import { api } from "@/lib/api";
import { useApiQuery } from "@/lib/use-api-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/file-link/:projectId?path=`: where a Seafile file link in a ticket text leads. The id
 * (not the slug) survives a project rename; existence and access are checked on click.
 */

export const Route = createFileRoute("/_app/file-link/$id")({
  validateSearch: (s: Record<string, unknown>) => ({
    path: typeof s.path === "string" ? s.path : "",
  }),
  component: FileLinkPage,
});

type LinkState =
  | { state: "file"; url: string }
  | { state: "folder"; path: string }
  | { state: "missing"; path: string }
  | { state: "forbidden" }
  | { state: "no-files" };

function FileLinkPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { path } = Route.useSearch();
  const navigate = useNavigate();
  const { data, error } = useApiQuery(
    () =>
      api.get<LinkState>(
        `/api/projects/${encodeURIComponent(id)}/files/link?path=${encodeURIComponent(path)}`,
      ),
    [id, path],
  );

  useEffect(() => {
    if (data?.state === "file") window.location.replace(data.url);
    if (data?.state === "folder") {
      void navigate({
        to: "/projects/$slug/files",
        params: { slug: id },
        search: { path: data.path },
        replace: true,
      });
    }
  }, [data, id, navigate]);

  let message = t("projFiles.linkLoading");
  if (error) message = t("projFiles.linkFailed");
  else if (data?.state === "forbidden") message = t("projFiles.linkForbidden");
  else if (data?.state === "missing") message = t("projFiles.linkMissing");
  else if (data?.state === "no-files") message = t("projFiles.linkNoFiles");
  const failed = !!error || (data && data.state !== "file" && data.state !== "folder");

  return (
    <div className="space-y-1 p-4 text-sm" data-testid="file-link-result">
      <p className={failed ? "text-destructive" : "text-muted-foreground"}>{message}</p>
      {data?.state === "missing" && (
        <p className="font-mono text-xs text-muted-foreground" data-testid="file-link-path">
          {data.path}
        </p>
      )}
    </div>
  );
}
